import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";

import { Box, IconButton, Paper, Typography, Button } from "@mui/material";
import { Add, Download } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { translate } from "../../../../translate/translate";
import { BaseService } from "../../../../services/base-service";
import { URLS } from "../../../../services/app-urls";
import { ErrorComponent } from "../../../../components/error/error.component";
import { Analysis } from "../../../../models/general/analysis";

const AnalysisErrorPageComponent = () => {
    const service = new BaseService<Analysis>(URLS.ANALYSIS);
    const serviceAnalysisLog = new BaseService<Blob>(URLS.ANALYSIS);
    const navigate = useNavigate();
    const [forecastName, setForecastName] = useState<string>();
    const { action: analysisId } = useParams();
    const [analysis, setAnalysis] = useState<Analysis | null>(null);
    const [logName] = useState<string>("Analysis_Error_Log.txt");

    const getAnalysisInfo = () => {
        if (!analysisId) return;

        service.clearParameters();
        service.getById(analysisId)
            .then(({ data }) => {
                setAnalysis(data);
                setForecastName(data?.description);
            })
            .catch((error) => ErrorComponent(error));
    };

    useEffect(() => {
        getAnalysisInfo();
    }, []);

    const styleTitle = {
        fontSize: 16,
        fontFamily: "Inter",
        fontWeight: "500",
        marginTop: "-40px"
    };

    const styleBody = {
        fontSize: 14,
        fontWeight: "500",
    };

    const redirect = () => {
        navigate("/general/analysis", { replace: true });
    };

    const newAnalysis = () => {
        navigate("/general/analysis/create", { replace: true });
    };

    const downloadErrorLog = async () => {
        try {
            serviceAnalysisLog.clearParameters();
            const response = await serviceAnalysisLog.getAnalysisErrorLog(analysis?.id);

            const blob = new Blob([response.data], { type: response.headers["content-type"] as string });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", "Analysis_Error_Log.txt");
            document.body.appendChild(link);
            link.click();

            link.parentNode?.removeChild(link);
        } catch (error) {
            const axiosError = error as AxiosError;
            ErrorComponent(axiosError, false);
        }
    };

    return (
        <section>
            <div className="floating-title floating-title-item" style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <IconButton onClick={redirect} id="back-analysis-page">
                    <ArrowBackIcon color="primary" fontSize="large" fontWeight="fontWeightBold" />
                </IconButton>
                <Typography variant={"h5"} id={forecastName}>
                    {forecastName}
                </Typography>
            </div>
            <Paper>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 10 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 5, textAlign: "center" }}>
                        <WarningAmberIcon color={"error"} sx={{width: "2em", height: "2em"}} />
                    </Box>
                    <Typography color="error" style={styleTitle}>
                        {translate.t("error_processing")}
                    </Typography>
                    <Typography variant="body1" color="var(--blue-gray-600)" style={styleBody}>
                        {translate.t("download_message")}
                    </Typography>
                    <Typography variant="body1" color="var(--blue-gray-300)" style={styleBody} sx={{margin: 1}}>
                        {logName}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center", gap: 1, textAlign: "center"}}>
                        <Button
                            startIcon={<Add/>}
                            color="primary"
                            variant="contained"
                            sx={{marginTop: "20px"}}
                            onClick={() => {
                                newAnalysis();
                            }}>

                            {translate.t("create_new_analysis")}</Button>
                        <Button
                            startIcon={<Download />}
                            color="error"
                            variant="contained"
                            sx={{marginTop: "20px"}}
                            onClick={() => {
                                downloadErrorLog();
                            }}>

                            {translate.t("download_log")}</Button>
                    </Box>
                </Box>
            </Paper>
        </section>
    );
};

export default AnalysisErrorPageComponent;
